import React, { useEffect, useState } from "react";
import axios from "axios";
import Popup from "reactjs-popup";
import { useSeriesShowContext } from "../SeriesShowApp";
import { formatWholeCents } from "../../../shared/teamCurrencyFormatting";
import { errorText, toastError, toastSuccess } from "../../../shared/toastHelper";
import { plural } from "../../SeriesEdit/updateDates";
import AddDatesModal from "./AddDatesModal";
import AddShowModal from "./AddShowModal";
import AddToCollectionModal from "./AddToCollectionModal";
import MembershipShowTypesModal from "./MembershipShowTypesModal";
import ConfirmDialog from "./ConfirmDialog";
import ConfirmPicker from "../../SeriesIndex/components/ConfirmPicker";
import SeriesDateResetModal from "../../shared/components/SeriesDateResetModal";
import { ownLabels } from "../../../shared/seriesOwnValues";
import { Badge, Button, LayersTwo01, Plus, Tabs, Toggle, Tooltip } from "@opendate-app/setlist";
import { CapacityMeter } from '@opendate-app/setlist';

const StatusPill = ({ status, classification }) => (
  <span className={`series-status-pill series-status-${classification || "default"}`}>
    {status}
  </span>
);

const MiniProgressBar = ({ sold, capacity }) => (
  <CapacityMeter size="sm" label="none" unit="tickets" taken={sold} capacity={capacity} />
);

const SeriesSettingsCell = ({ row }) => {
  if (row.pending) return null;
  if (row.own_values?.length > 0) {
    return (
      <Tooltip title="This date has its own value. Series changes ask before touching it.">
        <span tabIndex={0} className="series-own-trigger">
          <Badge color="warning" size="sm">{`Own: ${ownLabels(row.own_values).join(", ")}`}</Badge>
        </span>
      </Tooltip>
    );
  }
  if (row.just_added) return <Badge color="brand" size="sm">Just added</Badge>;
  return <span className="series-schedule-follows">Follows series</span>;
};

const ActionMenu = ({ row }) => {
  const {
    teamId, csrfToken, series, refreshSeries, handleRemoveRow, handleCancelRow, canCancelCalendarEvent, listedAsCollection,
  } = useSeriesShowContext();
  const noun = listedAsCollection ? "collection" : "series";
  const [confirmType, setConfirmType] = useState(null);
  const canReset = series.can_update === true && row.own_values?.length > 0 && row.upcoming && !!row.calendar_event_id;
  const editsDate = Boolean(series.tracks_own_values) && series.can_update === true;

  const closeConfirm = () => setConfirmType(null);

  return (
    <span onClick={(e) => e.stopPropagation()}>
      <Popup
        arrow={false}
        offsetY={5}
        position="bottom right"
        className="series-action-menu"
        trigger={(
          <button type="button" aria-label="Actions" className="series-action-trigger">
            <i className="fas fa-ellipsis-v" />
          </button>
        )}
      >
        {(close) => (
          <div>
            <button
              type="button"
              className="series-action-item"
              onClick={() => { close(); window.location = row.show_url; }}
            >
              <i className={`fal ${editsDate ? "fa-pen" : "fa-eye"} series-action-icon`} />
              <span>{editsDate ? "Edit this date" : "View event"}</span>
            </button>
            {canReset && (
              <button
                type="button"
                className="series-action-item"
                onClick={() => { close(); setConfirmType("reset"); }}
              >
                <i className="fal fa-layer-group series-action-icon" />
                <span>Reset to series</span>
              </button>
            )}
            {row.can_cancel && canCancelCalendarEvent && (
              <>
                <div className="series-action-divider" />
                <button
                  type="button"
                  className="series-action-item series-action-item-danger"
                  onClick={() => { close(); setConfirmType("cancel"); }}
                >
                  <i className="fal fa-ban series-action-icon-danger" />
                  <span>Cancel this event</span>
                </button>
              </>
            )}
            <button
              type="button"
              className="series-action-item series-action-item-danger"
              onClick={() => { close(); setConfirmType("remove"); }}
            >
              <i className="fal fa-times series-action-icon-danger" />
              <span>Remove from {noun}</span>
            </button>
          </div>
        )}
      </Popup>

      <ConfirmDialog
        open={confirmType === "cancel"}
        title="Cancel this event?"
        body="Are you sure you want to cancel this event in Opendate?"
        confirmLabel="Cancel event"
        onConfirm={() => handleCancelRow(row)}
        onClose={closeConfirm}
      />
      <ConfirmDialog
        open={confirmType === "remove"}
        title={`Remove from ${noun}?`}
        body={`Remove this date from the ${noun}? The event itself will not be canceled.`}
        confirmLabel="Remove date"
        onConfirm={() => handleRemoveRow(row)}
        onClose={closeConfirm}
      />
      {confirmType === "reset" && (
        <SeriesDateResetModal
          teamId={teamId}
          seriesId={series.id}
          seriesName={series.name}
          calendarEventId={row.calendar_event_id}
          startsAt={row.start_time}
          timeZone={series.venue_time_zone}
          currency={series.base_currency_symbol}
          csrfToken={csrfToken}
          onClose={closeConfirm}
          onReset={() => {
            closeConfirm();
            refreshSeries().catch(() => toastError("Could not refresh the series. Please reload the page."));
          }}
        />
      )}
    </span>
  );
};

const formatVenueDay = (time, timeZone) => new Date(time).toLocaleDateString("en-US", {
  month: "short",
  day: "numeric",
  ...(timeZone ? { timeZone } : {}),
});

const MembershipActionMenu = ({ membership }) => {
  const { handleRemoveMembership } = useSeriesShowContext();
  const [confirmingRemove, setConfirmingRemove] = useState(false);
  const [choosingShowTypes, setChoosingShowTypes] = useState(false);
  const narrowable = (membership.available_show_types || []).length > 0;

  return (
    <span onClick={(e) => e.stopPropagation()}>
      <Popup
        arrow={false}
        offsetY={5}
        position="bottom right"
        className="series-action-menu"
        trigger={(
          <button type="button" aria-label={`Actions for ${membership.name}`} className="series-action-trigger">
            <i className="fas fa-ellipsis-v" />
          </button>
        )}
      >
        {(close) => (
          <div>
            <button
              type="button"
              className="series-action-item"
              onClick={() => { close(); window.location = membership.show_url; }}
            >
              <i className="fal fa-eye series-action-icon" />
              <span>View series</span>
            </button>
            {narrowable && (
              <button
                type="button"
                className="series-action-item"
                data-testid={`membership-choose-show-types-${membership.id}`}
                onClick={() => { close(); setChoosingShowTypes(true); }}
              >
                <i className="fal fa-filter series-action-icon" />
                <span>Choose show types</span>
              </button>
            )}
            <div className="series-action-divider" />
            <button
              type="button"
              className="series-action-item series-action-item-danger"
              onClick={() => { close(); setConfirmingRemove(true); }}
            >
              <i className="fal fa-times series-action-icon-danger" />
              <span>Remove from collection</span>
            </button>
          </div>
        )}
      </Popup>

      <ConfirmDialog
        open={confirmingRemove}
        title={`Remove ${membership.name} from this collection?`}
        body="Its dates leave the collection and its passes stop covering them. Events you added on their own stay. The series itself is not changed."
        confirmLabel="Remove series"
        onConfirm={() => handleRemoveMembership(membership)}
        onClose={() => setConfirmingRemove(false)}
      />
      {narrowable && (
        <MembershipShowTypesModal
          membership={membership}
          open={choosingShowTypes}
          onClose={() => setChoosingShowTypes(false)}
        />
      )}
    </span>
  );
};

const MembershipRow = ({ membership, timeZone, currencySymbol, tracksOwnValues }) => {
  const range = membership.dates_count > 0
    ? ` · ${formatVenueDay(membership.first_start_time, timeZone)} – ${formatVenueDay(membership.last_start_time, timeZone)}`
    : "";
  return (
    <tr className="series-schedule-row" data-testid={`membership-row-${membership.id}`}>
      <td className="series-schedule-td-date">
        <div className="d-flex align-items-center series-schedule-membership-name">
          <a href={membership.show_url} className="series-schedule-date series-schedule-date-link series-schedule-name" title={membership.name}>
            {membership.name}
          </a>
          <Badge color="purple" size="sm" icon={LayersTwo01}>Whole series</Badge>
        </div>
        <div className="series-schedule-weekday">
          {membership.show_types
            ? `${membership.show_types.join(", ")} dates only. New ones join automatically.`
            : "New dates join automatically. Removed or canceled dates drop off."}
        </div>
      </td>
      <td className="series-schedule-td">
        <div>{`${membership.dates_count} ${membership.dates_count === 1 ? "date" : "dates"}${range}`}</div>
        <div className="series-schedule-weekday">{membership.admission_label}</div>
      </td>
      <td className="series-schedule-td series-schedule-td-tickets">
        <MiniProgressBar sold={membership.tickets_sold} capacity={membership.capacity} />
      </td>
      <td className="series-schedule-td series-schedule-td-gross text-end">
        {formatWholeCents(membership.gross_cents, currencySymbol)}
      </td>
      {tracksOwnValues && <td className="series-schedule-td" />}
      <td className="series-schedule-td series-schedule-td-actions">
        <MembershipActionMenu membership={membership} />
      </td>
    </tr>
  );
};

const ScheduleTable = () => {
  const {
    series, teamId, csrfToken, handleAttachConfirm, handleUpdateShowActivity, refreshSeries, applySeries, listedAsCollection,
  } = useSeriesShowContext();
  const isCanceled = series.state === "canceled";
  const schedule = series.schedule;
  const memberships = series.series_memberships || [];
  const addsWholeSeries = series.grouping && listedAsCollection;
  const [showAddModal, setShowAddModal] = useState(false);
  const [showConfirmPicker, setShowConfirmPicker] = useState(false);
  const [showAddToCollection, setShowAddToCollection] = useState(false);
  const [showAddDates, setShowAddDates] = useState(false);
  const [savingAutoPublish, setSavingAutoPublish] = useState(false);
  const addsDates = Boolean(series.adds_dates) && series.can_update === true;
  const datesUrl = `/teams/${teamId}/event_series/${series.id}/dates`;
  const [dateFilter, setDateFilter] = useState("all");
  const tracksOwnValues = Boolean(series.tracks_own_values);
  const ownCount = tracksOwnValues ? schedule.filter((row) => row.own_values?.length > 0).length : 0;
  // A whole series in a collection has no own values, so Own settings leaves it out.
  const ownOnly = ownCount > 0 && dateFilter === "own";
  const shownRows = ownOnly ? schedule.filter((row) => row.own_values?.length > 0) : schedule;

  useEffect(() => {
    if (ownCount === 0) setDateFilter("all");
  }, [ownCount]);

  const openAdd = () => {
    if (addsWholeSeries) return setShowAddToCollection(true);
    if (series.grouping) return setShowConfirmPicker(true);
    return setShowAddModal(true);
  };

  const attachSelected = async (list) => {
    // Continue on error so one show that can't attach doesn't strand the rest,
    // and refresh the dashboard once at the end rather than per show.
    const failed = [];
    let attached = 0;
    let anyServerError = false;
    for (const confirm of list) {
      try {
        await handleAttachConfirm(confirm.id, { refresh: false });
        attached += 1;
      } catch (e) {
        failed.push(confirm.title);
        // 422 is the same-time uniqueness collision; anything else is a real
        // server/network failure and must not be reported as a collision.
        if (e?.response?.status !== 422) anyServerError = true;
      }
    }
    setShowConfirmPicker(false);
    if (attached > 0) await refreshSeries();
    if (failed.length) {
      const reason = isCanceled
        ? "This series has been cancelled."
        : anyServerError
          ? "Please try again."
          : "It may already be in this collection.";
      toastError(`Couldn't add: ${failed.join(", ")}. ${reason}`);
    }
  };

  const fetchDatesPreview = async (params) => (await axios.get(`${datesUrl}/new.json`, { params })).data;

  // The response is the dashboard with the new dates on it, so no second fetch can fail after the dates are added.
  const addDates = async (params) => {
    let data;
    try {
      ({ data } = await axios.post(`${datesUrl}.json`, params, { headers: { "X-CSRF-Token": csrfToken } }));
    } catch (e) {
      toastError(errorText(e, "Couldn't add these dates. Please try again."));
      return;
    }
    const { addedDates: { count, published }, ...dashboard } = data;
    setShowAddDates(false);
    applySeries(dashboard);
    toastSuccess(`${count} ${plural(count, "date")} added. ${published
      ? "Built from the current series settings and published."
      : "Built from the current series settings. Saved as drafts."}`);
  };

  const setAutoPublish = (checked) => {
    setSavingAutoPublish(true);
    handleUpdateShowActivity("auto_publish_new_dates", checked)
      .catch((e) => toastError(errorText(e, "Couldn't save the auto-publish setting.")))
      .finally(() => setSavingAutoPublish(false));
  };

  return (
    <div className="series-schedule-card">
      <div className="series-schedule-header d-flex align-items-center justify-content-between">
        <div className="d-flex align-items-center series-schedule-heading">
          <h4 className="series-schedule-title mb-0">Events in Collection</h4>
          {ownCount > 0 && (
            <Tabs
              variant="button-border"
              size="sm"
              collapseBelow={0}
              aria-label="Dates"
              items={[{ value: "all", label: "All dates" }, { value: "own", label: "Own settings", count: ownCount }]}
              value={dateFilter}
              onChange={setDateFilter}
            />
          )}
        </div>
        {addsDates && (
          <div className="d-flex align-items-center series-schedule-header-actions">
            <Toggle
              size="sm"
              type="label"
              label="Auto-publish new dates"
              checked={series.auto_publish_new_dates !== false}
              disabled={savingAutoPublish}
              onChange={(e) => setAutoPublish(e?.target ? e.target.checked : e)}
            />
            <Button variant="primary" size="sm" icon={Plus} onClick={() => setShowAddDates(true)}>
              Add Dates
            </Button>
          </div>
        )}
        {!isCanceled && !series.setup_wizard && (
          <Button
            variant="primary"
            size="sm"
            icon={Plus}
            onClick={openAdd}
          >
            {addsWholeSeries ? "Add to collection" : "Add Event"}
          </Button>
        )}
      </div>
      {series.setup_wizard && schedule.some((row) => row.pending) && (
        <p className="series-schedule-progress-note" role="status" aria-live="polite">
          {`Creating dates: ${schedule.filter((row) => !row.pending).length} of ${schedule.length}. You can leave this page; they keep building.`}
        </p>
      )}
      {showAddDates && (
        <AddDatesModal
          seriesName={series.name}
          schedule={schedule}
          timeZone={series.venue_time_zone}
          currency={series.base_currency_symbol}
          autoPublish={series.auto_publish_new_dates !== false}
          ticketSets={series.ticket_sets || []}
          fetchPreview={fetchDatesPreview}
          onConfirm={addDates}
          onCancel={() => setShowAddDates(false)}
        />
      )}
      {addsWholeSeries && (
        <AddToCollectionModal open={showAddToCollection} onClose={() => setShowAddToCollection(false)} />
      )}
      {series.grouping ? (
        <ConfirmPicker
          open={showConfirmPicker}
          onClose={() => setShowConfirmPicker(false)}
          teamId={teamId}
          excludeSeriesId={series.id}
          multiSelect
          title="Add shows to this collection"
          confirmLabel="Add"
          onConfirm={attachSelected}
        />
      ) : (
        <AddShowModal open={showAddModal} onClose={() => setShowAddModal(false)} />
      )}
      <table className="table table-hover mb-0 series-schedule-table">
        <thead>
          <tr>
            <th className="series-schedule-th series-schedule-th-first">{series.grouping ? "Event" : "Date"}</th>
            <th className="series-schedule-th">Status</th>
            <th className="series-schedule-th">Tickets Sold</th>
            <th className="series-schedule-th text-end">Gross</th>
            {tracksOwnValues && <th className="series-schedule-th">Series settings</th>}
            <th></th>
          </tr>
        </thead>
        <tbody>
          {!ownOnly && memberships.map((membership) => (
            <MembershipRow
              key={`membership-${membership.id}`}
              membership={membership}
              timeZone={series.venue_time_zone}
              currencySymbol={series.base_currency_symbol}
              tracksOwnValues={tracksOwnValues}
            />
          ))}
          {shownRows.map(row => {
            const isPending = row.pending;
            // Formatted in the VENUE's zone, not the viewer's: what night a show is
            // belongs to the venue, and a west-coast operator opening an east-coast
            // collection after 9pm local would otherwise read every date a day early.
            // venue_time_zone is an IANA identifier (tzinfo_identifier) and is nil when
            // the series has no venue ownership, where the browser zone is the only
            // thing left to fall back to. For a collection this is now the row's ONLY
            // date, which is what makes it worth getting right here. Not shared
            // formatDateOnly: it takes no zone, and widening it is unsafe because
            // MetaAdsDetails passes it straight to a Recharts tickFormatter, which
            // would hand an index integer to whatever the second parameter became.
            const dateLabel = new Date(row.start_time).toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
              year: "numeric",
              ...(series.venue_time_zone ? { timeZone: series.venue_time_zone } : {}),
            });
            // A collection is hand-picked shows, each with its own billing, so the
            // name is what tells the rows apart. A recurring series is the same show
            // repeating, where the date is the only thing that differs — and a
            // pending slot has no show yet, so it falls back to the date either way.
            const timeLabel = new Date(row.start_time).toLocaleTimeString("en-US", {
              hour: "numeric",
              minute: "2-digit",
              ...(series.venue_time_zone ? { timeZone: series.venue_time_zone } : {}),
            });
            const showsName = series.grouping && !!row.name;
            const primary = showsName ? row.name : dateLabel;
            const entryTimes = series.entry_times?.[Math.floor(new Date(row.start_time).getTime() / 1000)];
            const clock = (seconds) => new Date(seconds * 1000).toLocaleTimeString("en-US", {
              hour: "numeric",
              minute: "2-digit",
              ...(series.venue_time_zone ? { timeZone: series.venue_time_zone } : {}),
            });
            const whenLabel = !entryTimes?.length
              ? timeLabel
              : entryTimes.length === 1
                ? `Entry at ${clock(entryTimes[0])}`
                : `${entryTimes.length} entry times · ${clock(entryTimes[0])} – ${clock(entryTimes[entryTimes.length - 1])}`;
            const secondary = [addsWholeSeries ? "Single event" : null, showsName ? dateLabel : row.weekday, whenLabel].filter(Boolean).join(" · ");
            return (
              <tr key={row.id} className="series-schedule-row">
                <td className="series-schedule-td-date">
                  {isPending ? (
                    <div className={`series-schedule-date${showsName ? " series-schedule-name" : ""}`} title={showsName ? primary : undefined}>
                      {primary}
                    </div>
                  ) : (
                    <a
                      href={row.show_url}
                      className={`series-schedule-date series-schedule-date-link${showsName ? " series-schedule-name" : ""}`}
                      title={showsName ? primary : undefined}
                    >
                      {primary}
                    </a>
                  )}
                  <div className="series-schedule-weekday">{secondary}</div>
                </td>
                <td className="series-schedule-td">
                  <StatusPill status={row.status} classification={row.classification} />
                </td>
                <td className="series-schedule-td series-schedule-td-tickets">
                  {isPending
                    ? <i className="fas fa-spinner fa-spin series-schedule-spinner" />
                    : <MiniProgressBar sold={row.tickets_sold} capacity={row.capacity} />}
                </td>
                <td className="series-schedule-td series-schedule-td-gross text-end">
                  {isPending ? <i className="fas fa-spinner fa-spin series-schedule-spinner" /> : formatWholeCents(row.gross_cents, series.base_currency_symbol)}
                </td>
                {tracksOwnValues && (
                  <td className="series-schedule-td">
                    <SeriesSettingsCell row={row} />
                  </td>
                )}
                <td className="series-schedule-td series-schedule-td-actions">
                  {isPending ? null : <ActionMenu row={row} />}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default ScheduleTable;
